import React, { useState } from 'react';

import { Skeleton, Typography } from '@mui/material';
import { AlineButton, AlineTooltip, Spacer } from 'src/components/generic';
import { OrgAvatar } from 'src/components/OrgAvatar';
import {
  AddCollaboratorDialogVariant,
  AddWorkflowCollaboratorDialog,
  TemplateCollaboratorsTab,
  useWorkflowReadOnlyContext,
} from 'src/components/workflow';
import { Apollo } from 'src/gql/generated';
import { tooltipMsg } from 'src/helpers';
import { useCheckWorkflowAuthorization, useGetWorkflowSimple } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
}));

interface WorkflowTeamTabToolbarProps {
  workflowId: UUID;
  templateTab?: TemplateCollaboratorsTab;
}

export function WorkflowTeamToolbar({
  templateTab,
  workflowId,
}: WorkflowTeamTabToolbarProps): JSX.Element {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const { isReadOnly } = useWorkflowReadOnlyContext();
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const { loading, workflow } = useGetWorkflowSimple(workflowId);
  const title =
    templateTab === TemplateCollaboratorsTab.TemplateCollaborators
      ? 'Template collaborators'
      : 'Workflow collaborators';
  const disabledReason = isReadOnly
    ? tooltipMsg.wFIsReadOnly
    : !canEditWorkflow
      ? 'Only workflow editors can manage collaborators'
      : '';
  const isContractWorkflow = workflow?.variant === Apollo.WorkflowVariant.Contract;

  if (loading || !workflow)
    return (
      <div className={classes.toolbar}>
        <Skeleton height={40} variant="circular" width={40} />
        <Skeleton variant="text" width={64} />
      </div>
    );

  return (
    <>
      <div className={classes.toolbar}>
        <OrgAvatar user={workflow.owner} />
        <Typography variant="subtitle1">{title}</Typography>
        <Spacer />
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              variant="outlined"
              onClick={() => setOpen(!open)}
            >
              Add Collaborator
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
      <AddWorkflowCollaboratorDialog
        open={open}
        permissionProps={{
          timeliness:
            templateTab === TemplateCollaboratorsTab.WorkflowCollaborators
              ? Apollo.WorkflowPermissionTimeliness.Queued
              : Apollo.WorkflowPermissionTimeliness.Active,
        }}
        templateTimelinessCheck={
          templateTab === TemplateCollaboratorsTab.WorkflowCollaborators
            ? Apollo.WorkflowPermissionTimeliness.Queued
            : Apollo.WorkflowPermissionTimeliness.Active
        }
        variant={
          isContractWorkflow || templateTab === TemplateCollaboratorsTab.TemplateCollaborators
            ? AddCollaboratorDialogVariant.NoTabs
            : AddCollaboratorDialogVariant.Tabs
        }
        workflow={workflow}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
